/**
 * Team vault grants — the rules the admin UI renders, kept out of JSX so they
 * can be tested for what they actually decide.
 *
 * WHY `scope` IS AN ENUM AND NOT A NULLABLE `folder_id`.
 *
 * In an ITEM, a null `folder_id` means "the team root" — a real place that
 * holds items. In a GRANT it used to mean "the whole vault". Overloading NULL
 * that way had two consequences, both bad: there was NO WAY to grant "team root
 * only" (asking for it silently granted everything), and a naive
 * `WHERE folder_id = item.folder_id` against a root item compares NULL = NULL,
 * which in SQL is NULL — it neither matches nor errors, so depending on how it
 * happened to be written it either 403'd or fell through to the whole-vault row
 * and OVER-GRANTED.
 *
 * So there are three distinct scopes and the UI must keep them distinct:
 *
 *   'vault'  — everything in this team's vault, including folders added later
 *   'root'   — items sitting directly in the team root, and nothing else
 *   'folder' — one named folder, and nothing else
 *
 * WHAT THIS FILE IS NOT. None of it is an access check. The server resolves
 * permission on every request and is the only authority; this decides what to
 * OFFER, so an admin is not shown a control whose effect it cannot describe.
 */

/** Scope values the picker uses. `folder:<id>` keeps the id inside one string. */
export const SCOPE_VAULT = 'vault';
export const SCOPE_ROOT = 'root';
export const folderScopeValue = (folderId) => `folder:${folderId}`;

/** `{ scope, folderId }` for a picker value. Unknown values fall back to root —
 *  the NARROWEST scope, so a parse failure can never widen access. */
export function parseGrantScopeValue(value) {
  if (value === SCOPE_VAULT) return { scope: SCOPE_VAULT, folderId: null };
  if (typeof value === 'string' && value.startsWith('folder:')) {
    const folderId = value.slice('folder:'.length);
    if (folderId) return { scope: 'folder', folderId };
  }
  return { scope: SCOPE_ROOT, folderId: null };
}

/** The picker value for an existing grant — how a stored grant maps back. */
export function grantScopeValue(grant) {
  if (grant?.scope === SCOPE_VAULT) return SCOPE_VAULT;
  if (grant?.scope === 'folder' && grant.folder_id != null) return folderScopeValue(grant.folder_id);
  return SCOPE_ROOT;
}

/**
 * Scope options for one team, in widening-to-narrowing order.
 *
 * Folders are listed by id, so renaming a folder never silently re-points a
 * grant, and two folders with the same display name stay distinguishable.
 */
export function grantScopeOptions(folders) {
  return [
    { value: SCOPE_VAULT, kind: SCOPE_VAULT, folderId: null, folderName: null },
    { value: SCOPE_ROOT, kind: SCOPE_ROOT, folderId: null, folderName: null },
    ...(Array.isArray(folders) ? folders : [])
      .filter((f) => f && f.id != null)
      .map((f) => ({
        value: folderScopeValue(f.id),
        kind: 'folder',
        folderId: String(f.id),
        folderName: f.name || '',
      })),
  ];
}

/** This member's grants. Ids are compared as strings — the API returns numbers
 *  and member lists have been seen carrying both. */
export function grantsForMember(grants, userId) {
  if (userId == null) return [];
  return (Array.isArray(grants) ? grants : []).filter(
    (g) => g && String(g.user_id) === String(userId)
  );
}

/**
 * Owners and admins hold implicit full access and cannot be reduced by a grant.
 *
 * Showing them an empty grant list would read as "this person has no access",
 * which is the opposite of the truth. The check is hoisted above membership
 * server-side for a related reason: `team_has_member?/2` is a pure
 * `team_members` lookup and does not consult `teams.owner_id`, so an owner
 * whose membership row is missing would otherwise be locked out of their own
 * team vault.
 */
export function hasImplicitVaultAccess(member) {
  return member?.role === 'owner' || member?.role === 'admin';
}

/**
 * May the CURRENT user administer this team's grants?
 *
 * Answered by the server (`can_administer` on `GET /folders`), not derived from
 * the role string. Roles in this codebase are messy — no enum, no check
 * constraint, two different validation lists, plus a legacy `member` value —
 * and re-deriving the answer here is how the client and the server come to
 * disagree. `false` until the fetch lands, so the panel never flashes controls
 * the server will refuse.
 */
export function canAdministerGrants(teamEntry) {
  return !!teamEntry?.canAdminister;
}

/**
 * The request body for granting `permission` at `scopeValue`.
 *
 * `folder_id` is sent ONLY for a folder scope. Sending it alongside 'vault' or
 * 'root' is what re-creates the NULL overloading the enum exists to remove.
 */
export function buildGrantPayload(userId, scopeValue, permission) {
  const { scope, folderId } = parseGrantScopeValue(scopeValue);
  return {
    userId,
    scope,
    permission: permission === 'write' ? 'write' : 'read',
    ...(scope === 'folder' ? { folderId } : {}),
  };
}
